import { useState } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import { ArrowLeft, User } from 'lucide-react'
import { ProfileDropdown } from '@/components/Layout/ProfileDropdown'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs'
import { PersonalInfoCard } from '@/components/profile/PersonalInfoCard'
import { NotificationCard } from '@/components/profile/NotificationCard'
import { AvailabilityCard } from '@/components/profile/AvailabilityCard'
import { SecurityCard } from '@/components/profile/SecurityCard'
import { ScopeSection } from '@/components/profile/ScopeSection'
import { DigestPreferencesCard } from '@/components/profile/DigestPreferencesCard'
import { SupportHoursOverrideCard } from '@/components/profile/SupportHoursOverrideCard'
import { UserProfile, Account, AccountSettings, ConnectedIdentity } from '@/components/profile/types'
import appIcon from '@/assets/images/icon-128.png'

type SoundPreference = AccountSettings['sound_preference']

interface ProfileProps {
  user: UserProfile
  current_account: Account | null
  account_settings: AccountSettings | null
  language_options: Array<[string, string]>  // [label, value]
  timezone_options: Array<[string, string]>  // [label, value]
  security_identities?: ConnectedIdentity[]
  errors?: Record<string, string[]>
}

export default function Profile({
  user,
  current_account,
  account_settings,
  language_options,
  timezone_options,
  security_identities = [],
  errors,
}: ProfileProps) {
  const { flash, errors: pageErrors } = usePage<{ flash: { notice?: string }; errors?: Record<string, string[]> }>().props
  const [soundPreference, setSoundPreference] = useState<SoundPreference>(
    (account_settings?.sound_preference as SoundPreference) || 'chirp'
  )
  const [autoAwayMinutes, setAutoAwayMinutes] = useState<number | null>(user.auto_away_minutes)
  const getInitialTab = () => {
    if (typeof window === 'undefined') return 'settings'
    const param = new URL(window.location.href).searchParams.get('tab')
    return param === 'notifications' || param === 'security' ? param : 'settings'
  }

  const [activeTab, setActiveTab] = useState<'settings' | 'notifications' | 'security'>(getInitialTab())
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = (payload: { first_name: string; last_name: string; preferred_language: string; avatar_type: string; timezone: string | null }) => {
    setIsSubmitting(true)
    router.patch('/profile', { user: payload }, { onFinish: () => setIsSubmitting(false) })
  }

  const handleSoundChange = (preference: SoundPreference) => {
    setSoundPreference(preference)
    router.patch('/profile', { account_user: { sound_preference: preference } }, {
      preserveScroll: true,
    })
  }

  const handleAvatarUpload = (file: File) => {
    const formData = new FormData()
    formData.append('account_user[avatar]', file)

    router.patch('/profile', formData, {
      forceFormData: true,
      preserveScroll: true,
    })
  }

  const handlePasswordChange = (payload: { password: string; password_confirmation: string }) => {
    router.patch('/profile/password', { user: payload }, { preserveScroll: true })
  }

  const handleDisconnectIdentity = (id: string) => {
    router.delete(`/profile/identity/${id}`, { preserveScroll: true })
  }

  const handleTabChange = (value: string) => {
    const nextTab = (value === 'notifications' || value === 'security') ? value : 'settings'
    setActiveTab(nextTab)
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.searchParams.set('tab', nextTab)
      window.history.replaceState({}, '', url.toString())
    }
  }

  return (
    <>
      <Head title="Profile" />

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border bg-card">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={appIcon} alt="Banshee.chat" className="size-8 rounded-lg" />
              <h1 className="text-xl">
                <span className="font-bold">Banshee</span>
                <span className="font-semibold text-muted-foreground">.chat</span>
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <ProfileDropdown user={user} />
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-5xl mx-auto px-4 py-8">
          <div className="mb-6">
            <a
              href={current_account ? `/${current_account.slug}/inbox` : '/'}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <ArrowLeft className="size-4" />
              Back to Inbox
            </a>
          </div>

          <div className="flex items-start gap-3 mb-8">
            <User className="size-7 text-muted-foreground mt-0.5" />
            <div>
              <h2 className="text-3xl font-semibold">Profile</h2>
              <p className="text-sm text-muted-foreground">Manage your personal settings and preferences.</p>
            </div>
          </div>

          {flash?.notice && (
            <div className="mb-6 p-3 rounded-lg bg-primary/10 text-sm text-primary">
              {flash.notice}
            </div>
          )}

          <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
            <TabsList className="w-full justify-start gap-2 bg-transparent p-0 border-b border-border rounded-none h-auto">
              <TabsTrigger
                value="settings"
                className="cursor-pointer rounded-none border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground"
              >
                Settings
              </TabsTrigger>
              <TabsTrigger
                value="notifications"
                className="cursor-pointer rounded-none border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground"
              >
                Notifications
              </TabsTrigger>
              <TabsTrigger
                value="security"
                className="cursor-pointer rounded-none border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground"
              >
                Security
              </TabsTrigger>
            </TabsList>

            <TabsContent value="settings" className="space-y-8">
              <ScopeSection
                scope="global"
                title="Your account"
                description="Applies to you across every workspace you belong to."
              >
                <PersonalInfoCard
                  user={user}
                  current_account={current_account}
                  language_options={language_options}
                  timezone_options={timezone_options}
                  errors={errors}
                  onSubmit={handleSubmit}
                  onAvatarUpload={handleAvatarUpload}
                  isSubmitting={isSubmitting}
                />
              </ScopeSection>

              {account_settings && (
                <ScopeSection
                  scope="workspace"
                  title="This workspace"
                  description={current_account
                    ? `Settings for ${current_account.name} only.`
                    : 'Settings scoped to the workspace you are currently in.'}
                >
                  <SupportHoursOverrideCard
                    override={account_settings.support_hours}
                    accountSchedule={account_settings.account_support_hours}
                    effectiveTimezone={user.effective_timezone}
                  />
                </ScopeSection>
              )}
            </TabsContent>

            <TabsContent value="notifications" className="space-y-8">
              <ScopeSection
                scope="workspace"
                title="This workspace"
                description={current_account
                  ? `Settings for ${current_account.name} only.`
                  : 'Settings scoped to the workspace you are currently in.'}
              >
                <AvailabilityCard
                  autoAwayMinutes={autoAwayMinutes}
                  onAutoAwayChange={setAutoAwayMinutes}
                />
                {account_settings ? (
                  <>
                    <NotificationCard soundPreference={soundPreference as SoundPreference} onSoundChange={handleSoundChange} />
                    <DigestPreferencesCard
                      dailyEnabled={account_settings.daily_digest_enabled}
                      weeklyEnabled={account_settings.weekly_digest_enabled}
                      sendHour={account_settings.digest_send_hour}
                      timezone={account_settings.digest_timezone}
                      effectiveTimezone={account_settings.effective_digest_timezone}
                      timezoneOptions={timezone_options}
                    />
                  </>
                ) : (
                  <div className="rounded-lg border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground">
                    Notification preferences will be available once you're inside an account context.
                  </div>
                )}
              </ScopeSection>
            </TabsContent>

            <TabsContent value="security" className="space-y-8">
              <ScopeSection
                scope="global"
                title="Your account"
                description="Sign-in and identity settings apply across every workspace."
              >
                <SecurityCard
                  identities={security_identities}
                  errors={pageErrors}
                  onPasswordChange={handlePasswordChange}
                  onDisconnect={handleDisconnectIdentity}
                />
              </ScopeSection>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </>
  )
}
